import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Project } from '@/utils/pocketbase';

export interface WizardResult {
  name: string;
  projectId?: string;
  brief: {
    product: string;
    audience?: string;
    services?: string;
    goal?: string;
    contacts?: string;
  };
}

interface BuilderWizardProps {
  projects: Project[];
  isSubmitting: boolean;
  onSubmit: (result: WizardResult) => void;
  onCancel: () => void;
}

const NO_PROJECT = '__none__';

/**
 * Two-step create flow.
 *
 * Split so the first screen asks only what the user certainly knows — the site's name
 * — and the brief, which takes actual thought, gets a screen of its own. Only the
 * product description is mandatory: everything else the model can work around, and a
 * long required form is what stops people from ever pressing the button.
 */
export function BuilderWizard({ projects, isSubmitting, onSubmit, onCancel }: BuilderWizardProps) {
  const { t } = useTranslation('builder');
  const [step, setStep] = useState<0 | 1>(0);

  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState<string>(NO_PROJECT);
  const [product, setProduct] = useState('');
  const [audience, setAudience] = useState('');
  const [services, setServices] = useState('');
  const [goal, setGoal] = useState('');
  const [contacts, setContacts] = useState('');

  const canContinue = name.trim().length > 0;
  const canSubmit = canContinue && product.trim().length > 0;

  const submit = () => {
    if (!canSubmit || isSubmitting) return;
    onSubmit({
      name: name.trim(),
      projectId: projectId === NO_PROJECT ? undefined : projectId,
      brief: {
        product: product.trim(),
        audience: audience.trim() || undefined,
        services: services.trim() || undefined,
        goal: goal.trim() || undefined,
        contacts: contacts.trim() || undefined,
      },
    });
  };

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex flex-wrap items-center gap-[8px]">
        {([t('wizard.steps.about'), t('wizard.steps.brief')] as const).map((label, index) => (
          <div key={label} className="flex items-center gap-[8px]">
            <span
              className={
                'inline-flex h-[28px] min-w-[28px] items-center justify-center rounded-full px-[8px] font-manrope text-[13px] font-bold ' +
                (index <= step
                  ? 'bg-[var(--main-brand,#2197f1)] text-white'
                  : 'bg-[var(--grey-accent,#eeeeee)] text-[color:var(--grey-text,#969696)]')
              }
            >
              {index + 1}
            </span>
            <span
              className={
                'font-manrope text-[14px] font-semibold ' +
                (index <= step
                  ? 'text-[color:var(--black-text,#0d0d0d)]'
                  : 'text-[color:var(--grey-text,#969696)]')
              }
            >
              {label}
            </span>
            {index === 0 && <span className="mx-[4px] h-px w-[24px] bg-[var(--grey-accent,#eeeeee)]" />}
          </div>
        ))}
      </div>

      {step === 0 ? (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-name" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.name.label')}
            </Label>
            <Input
              id="builder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('wizard.name.placeholder')}
              maxLength={120}
              autoFocus
              className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.name.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-project" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.project.label')}
            </Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger id="builder-project">
                <SelectValue placeholder={t('wizard.project.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>{t('wizard.project.placeholder')}</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.project.hint')}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-[16px]">
          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-product" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.product.label')}
            </Label>
            <Textarea
              id="builder-product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={t('wizard.product.placeholder')}
              rows={3}
              maxLength={600}
              autoFocus
              className="rounded-xl border-[#eeeeee] text-[16px] resize-none"
            />
            <p className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.product.hint')}
            </p>
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-audience" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.audience.label')}
            </Label>
            <Input
              id="builder-audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder={t('wizard.audience.placeholder')}
              maxLength={300}
              className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
            />
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-services" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.services.label')}
            </Label>
            <Textarea
              id="builder-services"
              value={services}
              onChange={(e) => setServices(e.target.value)}
              placeholder={t('wizard.services.placeholder')}
              rows={2}
              maxLength={600}
              className="rounded-xl border-[#eeeeee] text-[16px] resize-none"
            />
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-goal" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.goal.label')}
            </Label>
            <Input
              id="builder-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder={t('wizard.goal.placeholder')}
              maxLength={200}
              className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
            />
          </div>

          <div className="flex flex-col gap-[6px]">
            <Label htmlFor="builder-contacts" className="text-[14px] text-[color:var(--grey-text,#565858)]">
              {t('wizard.contacts.label')}
            </Label>
            <Input
              id="builder-contacts"
              value={contacts}
              onChange={(e) => setContacts(e.target.value)}
              placeholder={t('wizard.contacts.placeholder')}
              maxLength={200}
              className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
            />
          </div>
        </div>
      )}

      <div className="flex justify-between gap-[8px]">
        <Button
          variant="brand-outline"
          size="brand"
          onClick={step === 0 ? onCancel : () => setStep(0)}
          disabled={isSubmitting}
        >
          {step === 0 ? t('wizard.cancel') : t('wizard.back')}
        </Button>

        {step === 0 ? (
          <Button variant="brand" size="brand" onClick={() => setStep(1)} disabled={!canContinue}>
            {t('wizard.next')}
          </Button>
        ) : (
          <Button variant="brand" size="brand" onClick={submit} disabled={!canSubmit || isSubmitting}>
            {t('wizard.submit')}
          </Button>
        )}
      </div>
    </div>
  );
}
